<template>
  <section
class="invoice-toolbar"
aria-label="Invoice list controls"
>
    <div class="invoice-toolbar__primary">
      <div class="invoice-toolbar__search">
        <feather-icon
icon="SearchIcon"
size="18"
/>
        <b-form-input
          :value="search"
          type="search"
          placeholder="Search by customer, job, or invoice number"
          aria-label="Search invoices"
          @input="$emit('update:search', $event)"
        />
      </div>

      <div class="invoice-toolbar__actions">
        <b-button
          variant="outline-primary"
          :aria-expanded="filtersOpen ? 'true' : 'false'"
          @click="filtersOpen = !filtersOpen"
        >
          <feather-icon
icon="FilterIcon"
class="mr-50"
/>
          Filters
          <b-badge
            v-if="activeFilterCount"
            variant="primary"
            pill
            class="ml-50"
          >
            {{ activeFilterCount }}
          </b-badge>
        </b-button>
        <b-button
          variant="success"
          :disabled="exporting"
          @click="$emit('export')"
        >
          <b-spinner
v-if="exporting"
small
class="mr-50"
/>
          <feather-icon
v-else
icon="DownloadIcon"
class="mr-50"
/>
          {{ exporting ? 'Preparing...' : 'Export CSV' }}
        </b-button>
      </div>
    </div>

    <transition name="filter-panel">
      <div
v-if="filtersOpen"
class="invoice-toolbar__filters"
>
        <b-form-group
label="Start Date"
:label-for="`${controlId}-start-date`"
>
          <b-form-datepicker
            :id="`${controlId}-start-date`"
            :value="startDate"
            reset-button
            @input="$emit('update:startDate', $event)"
          />
        </b-form-group>
        <b-form-group
label="End Date"
:label-for="`${controlId}-end-date`"
>
          <b-form-datepicker
            :id="`${controlId}-end-date`"
            :value="endDate"
            reset-button
            @input="$emit('update:endDate', $event)"
          />
        </b-form-group>
        <b-form-group
          v-if="showBranch"
          label="Branch"
          :label-for="`${controlId}-branch`"
        >
          <v-select
            :value="branch"
            :dir="direction"
            :options="branches"
            :reduce="option => option.value"
            :clearable="true"
            :input-id="`${controlId}-branch`"
            placeholder="All branches"
            @input="$emit('update:branch', $event || '')"
          />
        </b-form-group>
        <b-form-group label="Attachment status">
          <div class="invoice-toolbar__missing-filter">
            <b-form-checkbox
              :id="`${controlId}-missing-files`"
              :checked="missingFiles"
              switch
              @change="$emit('update:missingFiles', $event)"
            >
              <span>Missing files only</span>
            </b-form-checkbox>
            <small>Show invoices with attachment records missing from storage.</small>
          </div>
        </b-form-group>
      </div>
    </transition>

    <div
v-if="activeFilterCount"
class="invoice-toolbar__chips"
>
      <span class="invoice-toolbar__chips-label">Active filters:</span>
      <button
v-if="startDate"
type="button"
@click="$emit('update:startDate', '')"
>
        From {{ startDate }} <feather-icon
icon="XIcon"
size="14"
/>
      </button>
      <button
v-if="endDate"
type="button"
@click="$emit('update:endDate', '')"
>
        To {{ endDate }} <feather-icon
icon="XIcon"
size="14"
/>
      </button>
      <button
v-if="branch"
type="button"
@click="$emit('update:branch', '')"
>
        {{ branchLabel }} <feather-icon
icon="XIcon"
size="14"
/>
      </button>
      <button
        v-if="missingFiles"
        type="button"
        class="invoice-toolbar__missing-chip"
        @click="$emit('update:missingFiles', false)"
      >
        <feather-icon
          icon="AlertTriangleIcon"
          size="14"
        />
        Missing files <feather-icon
          icon="XIcon"
          size="14"
        />
      </button>
      <b-button
variant="flat-secondary"
size="sm"
@click="clearFilters"
>
        Clear all
      </b-button>
    </div>

    <div class="invoice-toolbar__footer">
      <span>Rows per page</span>
      <v-select
        :value="perPage"
        :dir="direction"
        :options="perPageOptions"
        :clearable="false"
        aria-label="Rows per page"
        class="invoice-toolbar__page-size"
        @input="$emit('update:perPage', $event)"
      />
    </div>
  </section>
</template>

<script>
import {
  BBadge, BButton, BFormCheckbox, BFormDatepicker, BFormGroup, BFormInput, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'

export default {
  name: 'InvoiceListToolbar',
  components: {
    BBadge,
    BButton,
    BFormCheckbox,
    BFormDatepicker,
    BFormGroup,
    BFormInput,
    BSpinner,
    vSelect,
  },
  props: {
    controlId: { type: String, required: true },
    search: { type: String, default: '' },
    startDate: { type: String, default: '' },
    endDate: { type: String, default: '' },
    branch: { type: [String, Number], default: '' },
    branches: { type: Array, default: () => [] },
    showBranch: { type: Boolean, default: true },
    perPage: { type: Number, required: true },
    perPageOptions: { type: Array, required: true },
    direction: { type: String, default: 'ltr' },
    exporting: { type: Boolean, default: false },
    missingFiles: { type: Boolean, default: false },
  },
  data() {
    return {
      filtersOpen: false,
    }
  },
  computed: {
    activeFilterCount() {
      return [this.startDate, this.endDate, this.branch, this.missingFiles].filter(Boolean).length
    },
    branchLabel() {
      const match = this.branches.find(option => option.value === this.branch)
      return match ? match.label : `Branch ${this.branch}`
    },
  },
  methods: {
    clearFilters() {
      this.$emit('update:startDate', '')
      this.$emit('update:endDate', '')
      this.$emit('update:branch', '')
      this.$emit('update:missingFiles', false)
    },
  },
}
</script>

<style lang="scss" scoped>
.invoice-toolbar {
  padding: 1.25rem;
  border-bottom: 1px solid rgba(34, 41, 47, 0.08);
  background: rgba(248, 249, 250, 0.55);
}

.invoice-toolbar__primary,
.invoice-toolbar__actions,
.invoice-toolbar__footer,
.invoice-toolbar__chips {
  display: flex;
  align-items: center;
}

.invoice-toolbar__primary {
  justify-content: space-between;
  gap: 1rem;
}

.invoice-toolbar__search {
  position: relative;
  width: min(100%, 31rem);

  > svg {
    position: absolute;
    z-index: 2;
    top: 50%;
    left: 0.9rem;
    transform: translateY(-50%);
    color: #6e6b7b;
    pointer-events: none;
  }

  ::v-deep .form-control {
    padding-left: 2.7rem;
  }
}

.invoice-toolbar__actions {
  gap: 0.75rem;
  flex-shrink: 0;

  ::v-deep .btn {
    white-space: nowrap;
  }
}

.invoice-toolbar__filters {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid rgba(34, 41, 47, 0.1);
  border-radius: 0.5rem;
  background: #fff;

  ::v-deep .form-group {
    margin-bottom: 0;
  }
}

.invoice-toolbar__missing-filter {
  min-height: 2.7rem;
  padding: 0.55rem 0.7rem;
  border: 1px solid rgba(234, 84, 85, 0.22);
  border-radius: 0.4rem;
  background: rgba(234, 84, 85, 0.06);

  small {
    display: block;
    margin-top: 0.2rem;
    color: #6e6b7b;
    line-height: 1.25;
  }
}

.invoice-toolbar__chips {
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.85rem;

  button:not(.btn) {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.35rem 0.65rem;
    border: 1px solid rgba(115, 103, 240, 0.25);
    border-radius: 999px;
    background: rgba(115, 103, 240, 0.1);
    color: #5e50ee;
    cursor: pointer;
  }

  .invoice-toolbar__missing-chip {
    border-color: rgba(234, 84, 85, 0.3);
    background: rgba(234, 84, 85, 0.1);
    color: #d83a3b;
  }
}

.invoice-toolbar__chips-label,
.invoice-toolbar__footer {
  color: #6e6b7b;
  font-size: 0.875rem;
}

.invoice-toolbar__footer {
  gap: 0.65rem;
  margin-top: 1rem;
}

.invoice-toolbar__page-size {
  width: 6rem;
}

.filter-panel-enter-active,
.filter-panel-leave-active {
  transition: opacity 180ms ease, transform 180ms ease;
}

.filter-panel-enter,
.filter-panel-leave-to {
  opacity: 0;
  transform: translateY(-0.35rem);
}

@media (max-width: 767.98px) {
  .invoice-toolbar {
    padding: 1rem;
  }

  .invoice-toolbar__primary {
    align-items: stretch;
    flex-direction: column;
  }

  .invoice-toolbar__search {
    width: 100%;
  }

  .invoice-toolbar__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .invoice-toolbar__filters {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 768px) and (max-width: 1199.98px) {
  .invoice-toolbar__primary {
    align-items: stretch;
    flex-direction: column;
  }

  .invoice-toolbar__search {
    width: 100%;
  }

  .invoice-toolbar__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .invoice-toolbar__filters {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 399.98px) {
  .invoice-toolbar {
    padding: 0.75rem;
  }

  .invoice-toolbar__actions {
    grid-template-columns: 1fr;
  }

  .invoice-toolbar__chips-label {
    flex-basis: 100%;
  }
}

@media (min-width: 1600px) {
  .invoice-toolbar {
    padding: 1.5rem;
  }

  .invoice-toolbar__search {
    width: min(100%, 38rem);
  }
}

@media (prefers-reduced-motion: reduce) {
  .filter-panel-enter-active,
  .filter-panel-leave-active {
    transition: none;
  }
}
</style>

<style lang="scss">
.dark-layout {
  .invoice-toolbar {
    border-bottom-color: rgba(255, 255, 255, 0.08);
    background: rgba(22, 29, 49, 0.72);
  }

  .invoice-toolbar__filters {
    border-color: rgba(255, 255, 255, 0.1);
    background: #283046;
  }

  .invoice-toolbar__chips-label,
  .invoice-toolbar__footer,
  .invoice-toolbar__search > svg {
    color: #b4b7bd;
  }
}
</style>
